import { useMemo, useState } from "react";
import { Pie } from "@visx/shape";
import { Group } from "@visx/group";
import { ParentSize } from "@visx/responsive";
import {
  ChartLegend,
  type LegendItem,
} from "../../../shared/components/ChartLegend/ChartLegend";
import type { FeedbackSummaryResponse } from "../../types";
import { formatNumber, formatPercentage } from "../../utils/formatters";
import styles from "./FeedbackChart.module.css";

const THUMBS_UP_COLOR = "#10b981";
const THUMBS_DOWN_COLOR = "#ef4444";
const NO_RATING_COLOR = "#d1d5db";

interface SliceData {
  label: string;
  value: number;
  color: string;
}

interface InnerDonutProps {
  slices: SliceData[];
  centerLabel: string;
  centerCaption: string;
  width: number;
  height: number;
}

function InnerDonut({ slices, centerLabel, centerCaption, width, height }: InnerDonutProps) {
  const size = Math.min(width, height);
  const radius = size / 2;
  const innerRadius = radius * 0.6;

  if (size <= 0) return null;

  return (
    <svg width={width} height={height} role="img" aria-label="Feedback summary donut chart">
      <Group top={height / 2} left={width / 2}>
        <Pie
          data={slices}
          pieValue={(d) => d.value}
          outerRadius={radius - 8}
          innerRadius={innerRadius}
          padAngle={0.02}
          cornerRadius={3}
        >
          {(pie) =>
            pie.arcs.map((arc) => (
              <path
                key={arc.data.label}
                d={pie.path(arc) ?? ""}
                fill={arc.data.color}
              />
            ))
          }
        </Pie>
        <text
          textAnchor="middle"
          dy="-0.2em"
          className={styles.centerLabel}
          fill="var(--colours-neutral-900, #111827)"
        >
          {centerLabel}
        </text>
        <text
          textAnchor="middle"
          dy="1.2em"
          className={styles.centerCaption}
          fill="var(--colours-neutral-500, #6b7280)"
        >
          {centerCaption}
        </text>
      </Group>
    </svg>
  );
}

export interface FeedbackChartProps {
  data: FeedbackSummaryResponse;
  totalThreads?: number;
}

const CHART_HEIGHT = 220;

export function FeedbackChart({ data, totalThreads }: FeedbackChartProps) {
  const [includeUnrated, setIncludeUnrated] = useState(false);
  const hasUnratedData = totalThreads != null && totalThreads > data.total_with_feedback;

  const noRatingCount =
    totalThreads != null ? Math.max(0, totalThreads - data.total_with_feedback) : 0;

  const slices = useMemo<SliceData[]>(() => {
    const result: SliceData[] = [];
    if (data.thumbs_up > 0 || data.thumbs_down > 0) {
      result.push(
        { label: "Thumbs up", value: data.thumbs_up, color: THUMBS_UP_COLOR },
        { label: "Thumbs down", value: data.thumbs_down, color: THUMBS_DOWN_COLOR },
      );
    }
    if (includeUnrated && noRatingCount > 0) {
      result.push({ label: "No rating", value: noRatingCount, color: NO_RATING_COLOR });
    }
    return result;
  }, [data.thumbs_up, data.thumbs_down, includeUnrated, noRatingCount]);

  const legendItems = useMemo<LegendItem[]>(() => {
    const items: LegendItem[] = [
      { label: "Thumbs up", color: THUMBS_UP_COLOR, value: formatNumber(data.thumbs_up) },
      { label: "Thumbs down", color: THUMBS_DOWN_COLOR, value: formatNumber(data.thumbs_down) },
    ];
    if (includeUnrated && noRatingCount > 0) {
      items.push({ label: "No rating", color: NO_RATING_COLOR, value: formatNumber(noRatingCount) });
    }
    return items;
  }, [data.thumbs_up, data.thumbs_down, includeUnrated, noRatingCount]);

  if (data.total_with_feedback === 0 && noRatingCount === 0) {
    return (
      <div className={styles.emptyState}>No feedback data for this period.</div>
    );
  }

  const centerLabel =
    data.positive_ratio != null ? formatPercentage(data.positive_ratio * 100) : "N/A";

  return (
    <div className={styles.container}>
      <div className={styles.summaryRow}>
        <span className={styles.totalLabel}>
          {formatNumber(data.total_with_feedback)} rated
          {totalThreads != null && ` of ${formatNumber(totalThreads)} threads`}
        </span>
        {hasUnratedData && (
          <select
            aria-label="Unrated threads filter"
            className={styles.viewSelect}
            value={includeUnrated ? "include" : "exclude"}
            onChange={(e) => setIncludeUnrated(e.target.value === "include")}
          >
            <option value="exclude">Exclude unrated threads</option>
            <option value="include">Include unrated threads</option>
          </select>
        )}
      </div>
      <div className={styles.chartArea}>
        <ParentSize>
          {({ width }) => (
            <InnerDonut
              slices={slices}
              centerLabel={centerLabel}
              centerCaption="positive"
              width={width}
              height={CHART_HEIGHT}
            />
          )}
        </ParentSize>
      </div>
      <ChartLegend items={legendItems} />
    </div>
  );
}
